import React, { useState, useRef, useEffect } from "react";
import "./narrowDropdownHierarchy.css";
import UpIcon from "./assets/upIcon.png";
import DownIcon from "./assets/downIcon.png";
import CloseIcon from "./assets/closeIcon.png"; // For the "x" button on chips
import CheckedIcon from "./assets/checkedIcon.png";
import UncheckedIcon from "./assets/uncheckedIcon.png";
import NarrowInputField from "./narrowInputField";
import SearchIcon from "./assets/searchIcon.png";

/**
 * items shape (hierarchical):
 * [
 *   {
 *     label: "Parent 1",
 *     value: "p1",
 *     children: [
 *       { label: "Child 1.1", value: "c1.1" },
 *       { label: "Child 1.2", value: "c1.2" }
 *     ]
 *   },
 *   { label: "Parent 2", value: "p2" }
 * ]
 *
 * items shape (flat):
 * [
 *   { label: "Option A", value: "A" },
 *   { label: "Option B", value: "B" }
 * ]
 *
 * Props:
 * - title: string
 * - placeholder: string
 * - subLabel: string (optional)
 * - items: array of objects
 * - isRequired: boolean
 * - value: string or array (depending on single/multiple)
 * - onChange: function(newValue)
 * - multiple: boolean
 * - hierarchical: boolean
 */
const NarrowDropdownHierarchy = ({
  title,
  placeholder,
  subLabel,
  items = [],
  isRequired,
  value,
  onChange,
  multiple = false,
  hierarchical = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Filter items based on searchTerm.
   * - If hierarchical, recursively filter children.
   * - If flat, filter top-level items by label.
   */
  const filterHierarchical = (list, term) => {
    return list
      .map((item) => {
        const children = item.children
          ? filterHierarchical(item.children, term)
          : [];
        const matchesItem = item.label
          .toLowerCase()
          .includes(term.toLowerCase());
        // Keep item if it matches OR any of its children match
        if (matchesItem || children.length > 0) {
          return { ...item, children };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredItems = hierarchical
    ? filterHierarchical(items, searchTerm)
    : items.filter((item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  /**
   * Handle selecting/deselecting an item.
   * - For multiple select, add/remove from array.
   * - For single select, set the value and close the dropdown.
   */
  const handleItemClick = (itemValue) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const alreadySelected = currentValues.includes(itemValue);
      let newValues;
      if (alreadySelected) {
        newValues = currentValues.filter((v) => v !== itemValue);
      } else {
        newValues = [...currentValues, itemValue];
      }
      onChange(newValues);
    } else {
      onChange(itemValue);
      setIsOpen(false);
    }
  };

  /**
   * Render selected items as chips (if multiple).
   * For single select, just render the selected label or placeholder.
   */
  const renderSelectedDisplay = () => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) {
        return <span className="dropdown-placeholder">{placeholder}</span>;
      }
      // Render each selected item as a chip
      return selectedValues.map((val) => {
        const item = findItemByValue(items, val);
        return (
          <div className="chip" key={val}>
            {item?.label || val}
            <img
              src={CloseIcon}
              alt="Remove"
              className="chip-remove"
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggling dropdown
                handleItemClick(val); // This will remove it
              }}
            />
          </div>
        );
      });
    } else {
      // Single select
      if (!value) {
        return <span className="dropdown-placeholder">{placeholder}</span>;
      }
      const item = findItemByValue(items, value);
      return <span className="dropdown-selected">{item?.label || value}</span>;
    }
  };

  /**
   * Recursively render items (if hierarchical).
   * Indent child items by level * 16px (example).
   */
  const renderItems = (list, level = 0) => {
    return list.map((item) => {
      const selectedValues = multiple
        ? Array.isArray(value)
          ? value
          : []
        : [value];
      const isSelected = selectedValues.includes(item.value);

      // If item has children and hierarchical is true, render them below
      if (hierarchical && item.children && item.children.length > 0) {
        return (
          <React.Fragment key={item.value}>
            <div
              className={`dropdown-item ${isSelected ? "selected" : ""}`}
              style={{ marginLeft: level * 16 }}
              onClick={() => handleItemClick(item.value)}
            >
              {multiple && (
                <img
                  src={isSelected ? CheckedIcon : UncheckedIcon}
                  alt={isSelected ? "Checked" : "Unchecked"}
                  className="dropdown-checkbox-icon"
                />
              )}
              <span className="dropdown-item-text">{item.label}</span>
            </div>
            {renderItems(item.children, level + 1)}
          </React.Fragment>
        );
      }

      // Flat item (or child with no children)
      return (
        <div
          key={item.value}
          className={`dropdown-item ${isSelected ? "selected" : ""}`}
          style={{ paddingLeft: level + 1 * 26 }}
          onClick={() => handleItemClick(item.value)}
        >
          {multiple && (
            <img
              src={isSelected ? CheckedIcon : UncheckedIcon}
              alt={isSelected ? "Checked" : "Unchecked"}
              className="dropdown-checkbox-icon"
            />
          )}
          <span className="dropdown-item-text">{item.label}</span>
        </div>
      );
    });
  };

  return (
    <div className="dropdown-field hierarchical" ref={containerRef}>
      {/* Label */}
      <label className="dropdown-label">
        {title} {isRequired && <span className="required">*</span>}
      </label>
      {/* Sublabel */}
      {subLabel && <div className="dropdown-sublabel">{subLabel}</div>}

      {/* Display area (chips or single selection) */}
      <div className="dropdown-display-flex" onClick={toggleDropdown}>
        <div className="chips-container">{renderSelectedDisplay()}</div>
        <span className="dropdown-arrow">
          <img
            src={isOpen ? UpIcon : DownIcon}
            alt={isOpen ? "Up arrow" : "Down arrow"}
            style={{ width: "24px", height: "24px" }}
          />
        </span>
      </div>

      {/* Dropdown menu with search + items */}
      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-search">
            <img src={SearchIcon} alt="Search Icon" className="search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="dropdown-search-input"
            />
          </div>
          <div className="dropdown-list">
            {filteredItems && filteredItems.length > 0 ? (
              renderItems(filteredItems)
            ) : (
              <div className="dropdown-item no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to find an item by its value (recursively).
 */
function findItemByValue(items, value) {
  for (const item of items) {
    if (item.value === value) return item;
    if (item.children) {
      const found = findItemByValue(item.children, value);
      if (found) return found;
    }
  }
  return null;
}

export default NarrowDropdownHierarchy;
